/**
 * Module dependencies.
 */

import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import pick from 'lodash/pick';
import { GetStaticProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { Control, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { SignUpData, signUp } from 'src/api/authentication/sign-up';
import { Button } from 'src/components/core/button';
import { CheckboxField } from 'src/components/core/forms/checkbox/field';
import { InputField } from 'src/components/core/forms/input/field';
import { PasswordField } from 'src/components/core/forms/input/password-field';
import { Link as DefaultLink } from 'src/components/core/links/link';
import { SEO } from 'src/components/core/seo';
import { IsFamiliarCompanyField } from 'src/components/forms/profile';
import { Description, Form, Link, Title } from 'src/components/layout/authentication/styles';
import { inputMasks } from 'src/core/constants/forms';
import { routes } from 'src/core/constants/routes';
import { emailRules, nifRules, passwordRules, requiredRule } from 'src/core/utils/fields-rules';
import { setFormServerErrors } from 'src/core/utils/forms';
import { getNetworkErrorTranslatedKey } from 'src/core/utils/requests';
import { cleanValue } from 'src/core/utils/strings';
import { toast } from 'src/providers/toast';
import { SSGProps } from 'src/types/app';
import styled from 'styled-components';

/**
 * `SignUpFormData` type.
 */

type SignUpFormData = {
  isFamiliarCompany: string;
  terms: boolean;
} & Omit<SignUpData, 'isFamiliarCompany'>;

/**
 * `StyledCheckboxField` styled component.
 */

const StyledCheckboxField = styled(CheckboxField)`
  margin: 8px 0 16px;
` as typeof CheckboxField;

/**
 * `TermsLink` styled component.
 */

const TermsLink = styled(DefaultLink).attrs({ as: 'a' })`
  margin: 8px 0 16px;
  text-decoration: underline;

  &:focus,
  &:focus-within,
  &:hover {
    color: var(--color-blue500);
  }
`;

/**
 * `SignUp` page.
 */

const SignUp: NextPage = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { control, formState, handleSubmit, setError } = useForm<SignUpFormData>({
    mode: 'onSubmit'
  });

  const { isSubmitting } = formState;
  const { isPending, mutate } = useMutation({ mutationFn: signUp });
  const onSubmit = useCallback(
    (data: SignUpFormData) => {
      mutate(
        {
          ...pick(data, ['email', 'password']),
          isFamiliarCompany: data?.isFamiliarCompany === 'true',
          nif: cleanValue(data?.nif)
        },
        {
          onError: networkError => {
            const error = networkError as AxiosError;
            const errorMessage = getNetworkErrorTranslatedKey({
              basePath: 'sign-up:',
              error
            });

            if (error?.response?.status === 422) {
              setFormServerErrors<SignUpData>(error as any, setError as any, t, 'sign-up:errors.fields.');
            }

            toast.error(t(errorMessage, { returnObjects: true }));
          },
          onSuccess: () => {
            toast.success({ title: t('sign-up:form.successMessage') });

            router.push(routes.authentication.signIn);
          }
        }
      );
    },
    [mutate, router, setError, t]
  );

  return (
    <>
      <SEO title={t('sign-up:metatags.title')} />

      <Title>{t('sign-up:title')}</Title>

      <Description>
        <Trans
          components={[<Link href={routes.authentication.signIn} key={'login-link'} />]}
          i18nKey={'sign-up:description'}
          transSupportBasicHtmlNodes
        />
      </Description>

      <Form autoComplete={'off'} onSubmit={handleSubmit(onSubmit)}>
        <InputField
          control={control}
          disabled={isSubmitting}
          label={t('auth:form.labels.nif')}
          mask={inputMasks.nif}
          name={'nif'}
          rules={nifRules(t)}
        />

        <InputField
          control={control}
          disabled={isSubmitting}
          label={t('auth:form.labels.email')}
          name={'email'}
          rules={emailRules(t)}
          type={'email'}
        />

        <PasswordField
          control={control}
          disabled={isSubmitting}
          helpText={t('auth:form.helpText.password')}
          label={t('sign-up:form.labels.password')}
          name={'password'}
          rules={passwordRules(t)}
        />

        <IsFamiliarCompanyField control={control as Control<any>} formState={formState} />

        <StyledCheckboxField
          control={control}
          disabled={isSubmitting}
          label={
            <Trans
              components={[
                <TermsLink href={process.env.NEXT_PUBLIC_TERMS_AND_CONDITIONS_URL} key={'terms-link'} />,
                <TermsLink href={process.env.NEXT_PUBLIC_PRIVACIES_URL} key={'privacy-link'} />
              ]}
              i18nKey={'sign-up:form.labels.terms'}
              transSupportBasicHtmlNodes
            />
          }
          name={'terms'}
          rules={requiredRule(t)}
        />

        <Button isLoading={isSubmitting || isPending} stretch style={{ marginBottom: 24 }} type={'submit'}>
          {t('sign-up:actions.submit')}
        </Button>
      </Form>
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSGProps> = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale as string, ['common', 'auth', 'sign-up'])),
      layout: 'auth'
    },
    revalidate: 60
  };
};

/**
 * Export `SignUp` page.
 */

export default SignUp;
